import { Injectable } from '@angular/core';

/**
 * Handles the backdrop cutout + element highlight effect used when opening
 * ticket action sheets. The backdrop is darkened everywhere except the
 * highlighted element, giving a spotlight effect.
 */
@Injectable({ providedIn: 'root' })
export class TicketBackdropHighlightService {
    private activeElement: HTMLElement | null = null;

    highlight(element: HTMLElement, borderRadius = 8): void {
        this.activeElement = element;
        element.style.position = 'relative';
        element.style.zIndex = '20000';

        const backdrop = document.querySelector('ion-backdrop') as HTMLElement | null;
        if (backdrop) {
            const rect = element.getBoundingClientRect();
            backdrop.style.clipPath = this.generateInvertedClipPath(rect, 0, borderRadius);
        }
    }

    reset(): void {
        if (this.activeElement) {
            this.activeElement.style.position = '';
            this.activeElement.style.zIndex = '';
            this.activeElement = null;
        }

        const backdrop = document.querySelector('ion-backdrop') as HTMLElement | null;
        if (backdrop) {
            backdrop.style.clipPath = '';
        }
    }

    private generateInvertedClipPath(rect: DOMRect, padding: number, borderRadius: number): string {
        const left = rect.left - padding;
        const right = rect.right + padding;
        const top = rect.top - padding;
        const bottom = rect.bottom + padding;
        const r = borderRadius;
        const steps = 8;
        const angleStep = Math.PI / 2 / (steps + 1);

        const trCorner: string[] = [];
        for (let i = 1; i <= steps; i++) {
            const angle = angleStep * i;
            trCorner.push(`${right - r + r * Math.sin(angle)}px ${top + r - r * Math.cos(angle)}px`);
        }
        const brCorner: string[] = [];
        for (let i = 1; i <= steps; i++) {
            const angle = angleStep * i;
            brCorner.push(`${right - r + r * Math.cos(angle)}px ${bottom - r + r * Math.sin(angle)}px`);
        }
        const blCorner: string[] = [];
        for (let i = 1; i <= steps; i++) {
            const angle = angleStep * i;
            blCorner.push(`${left + r - r * Math.sin(angle)}px ${bottom - r + r * Math.cos(angle)}px`);
        }
        const tlCorner: string[] = [];
        for (let i = 1; i <= steps; i++) {
            const angle = angleStep * i;
            tlCorner.push(`${left + r - r * Math.cos(angle)}px ${top + r - r * Math.sin(angle)}px`);
        }

        return `polygon(
            evenodd,
            0 0, 100vw 0, 100vw 100vh, 0 100vh, 0 0,
            ${left + r}px ${top}px,
            ${right - r}px ${top}px,
            ${trCorner.join(', ')},
            ${right}px ${top + r}px,
            ${right}px ${bottom - r}px,
            ${brCorner.join(', ')},
            ${right - r}px ${bottom}px,
            ${left + r}px ${bottom}px,
            ${blCorner.join(', ')},
            ${left}px ${bottom - r}px,
            ${left}px ${top + r}px,
            ${tlCorner.join(', ')},
            ${left + r}px ${top}px
        )`;
    }
}
